import React, { Component } from 'react'
import { get as _get, isFunction, set as _set } from 'lodash'
import validator from 'validator'

import { moment, validators } from './ext-refs'
// Optimization for lodash, import what is needed only

const StateContext = React.createContext();
const { Provider, Consumer } = StateContext;
const delayedValidationTimer = {};
const asyncValidatorsCurrentlyRunning = {};


/**
 * HOC to add validation and state to some kind of input field.
 * The wrapped field expects the following properties:
 *   name
 *   postProcess - optional
 *   .. all other properties will passed through.
 * Field will receive the following properties from the HOC.
 * If they where provided to the wrapped field, they will be replaced:
 *   value
 *   validationState
 *   onChange
 *   onBlur
 * @return function(*): * Wrapped Component
 */
function StateWrapper(Field) {
    const NewField = class extends Component {
        constructor(props) {
            super(props);
            this.onChange = this.onChange.bind(this);
            this.onBlur = this.onBlur.bind(this);
        }

        onChange(e) {
            const { state, postProcess, onChange: myOnChange } = this.props;
            const { onChange } = state;
            onChange(e, postProcess);
            if (isFunction(myOnChange)) {
                myOnChange(e)
            }
        }

        onBlur(e) {
            const { state, postProcess, onBlur: myOnBlur } = this.props;
            const { onBlur } = state;
            onBlur(e, postProcess);
            if (isFunction(myOnBlur)) {
                myOnBlur(e)
            }
        }

        componentDidMount() {
            const {state} = this.props,
                {registerComponent} = state;
            registerComponent(this);
        }

        componentWillUnmount() {
            const {state} = this.props,
                {deregisterComponent} = state;
            deregisterComponent(this);
        }

        render() {
            // extract postProcess.
            const {
                    state, name,
                    // these fields not use, but we want to remove them from restprops
                    // so we don't send them to the field
                    validations, postProcess,
                    ...restProps
                } = this.props,
                {data, validationState} = state,
                value = _get(data, name),
                vState = validationState[name];
            return (
                <Field
                    {...restProps}
                    name={name}
                    value={value}
                    validationState={vState}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                />
            );
        }
    };
    NewField.displayName = `StateWrapper.${Field.name}`;
    return (props) => (
        <Consumer>
            {state => <NewField {...props} state={state}/>}
        </Consumer>
    );
}

export {StateWrapper};

/**
 * HOC to add validation and state to some kind of input field.
 * This in the situation where the field handles onChange/onBlur locally,
 * exposing the value them selfs using setValue.
 * The wrapped field expects the following properties:
 *   name
 *   postProcess - optional
 *   .. all other properties will passed through.
 * Field will receive the following properties from the HOC.
 * If they where provided to the wrapped field, they will be replaced:
 *   value
 *   validationState
 *   setValue
 * @return function(*): * Wrapped Component
 */
function FieldWrapper(Field) {
    const NewField = class extends Component {
        componentDidMount() {
            const {state} = this.props,
                {registerComponent} = state;
            registerComponent(this);
        }

        componentWillUnmount() {
            const {state} = this.props,
                {deregisterComponent} = state;
            deregisterComponent(this);
        }

        render() {
            // extract postProcess.
            const {
                    state, name,
                    // these fields not use, but we want to remove them from restprops
                    // so we don't send them to the field
                    validations,
                    ...restProps
                } = this.props,
                {data, validationState, setValue} = state,
                value = _get(data, name),
                vState = validationState[name];
            return (
                <Field
                    {...restProps}
                    name={name}
                    value={value}
                    validationState={vState}
                    setValue={setValue}
                />
            );
        }
    };
    NewField.displayName = `FieldWrapper.${Field.name}`;
    return (props) => (
        <Consumer>
            {state => <NewField {...props} state={state}/>}
        </Consumer>
    );
}

export {FieldWrapper};

const luhnCheck = (cardNumber) => {
    let digits = cardNumber.length,
        digit = 0,
        sum = 0,
        oddOrEven = (digits & 1);
    for (let count = 0; count < digits; count++) {
        digit = Number(cardNumber.charAt(count));
        if (((count & 1) ^ oddOrEven) === 0) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
    }
    return (sum === 0) ? false : (sum % 10 === 0);
};
const validCreditCard = (cardNumber) => {
    const regexp = /^[0-9]+$/,
        rVISA = /^4/,
        rMC = /^(5[1-5]|2[2-7])/,
        rAMEX = /^3[47]/,
        rDISC = /^(6011|65)/;
    if (!regexp.test(cardNumber)) {
        return false;
    }
    let len = cardNumber.length;
    if (len < 13 || len > 19) {
        return false;
    }
    if (!luhnCheck(cardNumber)) {
        return false;
    }
    // check prefixes
    return rVISA.test(cardNumber) || rMC.test(cardNumber) || rAMEX.test(cardNumber) || rDISC.test(cardNumber);
};

/**
 * Similar check to what is done in WebValidatorBase#validateName
 * @param name
 * @param isRequired
 * @returns {boolean}
 */
const validName = (name, isRequired = true) => {
    /*
     If the field is not a required one (ex. middle name) short circuit the validation as a blank value will trip
     some of the other checks below such as not being an alpha numeric value
    */
    if (validators.isEmptyTrimmed(name) && !isRequired) {
        return true;
    }
    
    if (validators.isEmptyTrimmed(name)) {
        return false;
    }

    if (validators.containsCreditCardNumber(name)) {
        return false;
    }

    if (!validators.isAlphaNumericAndPunctuation(name)) {
        return false;
    }

    if (noInvalidCharacters(name)) {
        return false;
    }
    
    // should not contain all numbers
    if (validators.isNumeric(name.trim())) {
        return false;
    }

    return true;
};

const noInvalidCharacters = (value) => {
    /^[a-zA-Z0-9.,# ]+$/.test(value);
};

// Standard validators here...
const StandardValidators = {
    required: (value) => value,
    alphanumeric: (value) => /^[a-zA-Z0-9]*$/.test(value),
    alphaAndSpace: (value) => /^[a-zA-Z ]*$/.test(value),
    name: validName,
    noInvalidCharacters,
    integer: (value) => /^[+-]?[0-9]+$/.test(value),
    positive: (value) => /^[0-9]+$/.test(value) && !/^0+$/.test(value),
    numeric: (value) => /^[+-]?[0-9]+([.][0-9]+)?$/.test(value),
    email: (value) => !value || validator.isEmail(value),
    minLength2: (value) => ((value || '').length >= 2),
    validCreditCard,
    validExpiryDate: (value) => (/^\d{2}\/\d{2}$/.test(value) && moment(value, 'MM/YY').isSameOrAfter(moment().startOf('month'))),
    validRoutingNumberUS: (value) => /^\d{9}$/.test(value),
    validRoutingNumberCA: (value) => /^\d{9}$/.test(value),
    validPhoneNumber10d: (value) => /^\d{10}$/.test(value),
    validBankAccount: (value) => /^\d{3,17}$/.test(value),
    validCheckNumber: (value) => /^(?![a-zA-Z]*$)[a-zA-Z0-9]+$/.test(value), //alpha-numeric but not all letters
    zipCode: (value) => value ===  "" || /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(value) || /^\d{5}([-]?\d{4})?$/.test(value),
    alphaNumericAndPunctuation: (value) => !value || validators.isAlphaNumericAndPunctuation(value),
    notContainsCreditCardNumber: (value) => (!validators.containsCreditCardNumber(value)),
    greaterOrEqual: (value, args) => (value >= args.checkValue),
    lessThanOrEqual: (value, args) => (value <= args.checkValue),
    regexp: (value, args) => {
        if (!value) {
            return true;
        }
        let regexps = args.regexps;
        if (!Array.isArray(regexps)) {
            regexps = [regexps];
        }
        let result = true;
        regexps.forEach(regexp => {
            let patt = new RegExp(regexp);
            result &= patt.test(value);
        });
        return result;
    }
};

export {StandardValidators};

/**
 * This React component will handle applicaiton state and validation.
 * Built using React context. Use StateWrapper to embed individual UI components.
 */

class FormValidator extends Component {
    constructor(props) {
        super(props);
        // State object stores the state (field values and validation state)
        // This object also has a number of methods use to change the sate
        //
        this.state = {
            data: props.initialState || {},
            validationState: {},
            fields: {},
            config: props.config || {},
            /**
             * Methods that will be accessed by fields to hook into state change.
             */
            onChange: this.onChange.bind(this),
            onBlur: this.onBlur.bind(this),
            /**
             * Methods to register/deregister component visible on the screen.
             */
            registerComponent: this.registerComponent.bind(this),
            deregisterComponent: this.deregisterComponent.bind(this),
            // Utility methods working on the whole form
            validateAllSync: this.validateAllSync.bind(this),
            /**
             * Validate all visible (registered) fields
             * Will issue a state change event to React.
             */
            validateAll: this.validateAll.bind(this),
            /**
             * Validate field (name, value).
             * Return validate state, but not state change.
             */
            validateField: this.validateField.bind(this),
            /**
             * Checks if an async validator is currently running
             */
            checkAsyncValidation: this.checkAsyncValidation.bind(this),
            /**
             * Set errors for all fields in one transaction.
             * Issue state change event.
             */
            setErrors: this.setErrors.bind(this),
            /**
             * Set error for one field.
             */
            setErrorSync: this.setErrorSync.bind(this),
            /**
             * Reset all errors, or some.
             * Issue state change event.
             */
            resetErrors: this.resetErrors.bind(this),
            /**
             * Reset all errors for a given field
             */
            resetError: this.resetError.bind(this),
            /**
             * Helper method to retrieve a specific field by name.
             */
            getValue: this.getValue.bind(this),
            /**
             * Helper method to change current state of a single field by name.
             * will trigger validation and state change.
             */
            setValue: this.setValue.bind(this),
            /**
             * Helper method to change state as a single transaction
             * not one field at a time.
             */
            setValues: (f, callback) => {
                if(!callback){
                    this.setState((prevState) => ({
                        data: f(prevState.data)
                    }));
                } else {
                    this.setState((prevState) => ({
                        data: f(prevState.data)
                    }), ()=>callback());
                }
            },
            setConfigValues: (f) => {
                this.setState((prevState) => ({
                    config: f(prevState.config)
                }));
            }
        };
    }

    /**
     * Retrieve field value from state object.
     * @param name
     * @returns field value
     */
    getValue(name) {
        return _get(this.state.data, name);
    }

    /**
     * Set field value and trigger validation.
     * @param name
     * @param value
     * @param postProcess - post processing function
     * @param eventType - what type of event called this function. (optional)
     * Transactional
     */
    setValue(name, value, postProcess, eventType) {
        // Trigger async state change.
        this.setState((prevState) => {
            const field = prevState.fields[name] || {};
            prevState.pristine = false;
            prevState.fields[name] = field;
            _set(prevState.data, name, value);
            prevState.validationState[name] = this.validateField(name, value, eventType);
            (typeof postProcess === 'function') && postProcess(name, value, prevState.data, prevState.validationState, eventType);
            // For now we do not create copies of existing state.
            // Note that this contrary to React philosophy.
            return prevState;
        });
    }

    /**
     * Internal validate field method.
     * @param name
     * @param value
     * @param eventType - what type of event called this validation. (optional)
     * @returns new validation state
     * Not transaction, read, does not modify state.
     */
    validateField(name, value, eventType) {
        const field = this.state.fields[name] || {},
            validations = field.validations,
            data = this.state.data;
        if (!validations) return null;
        let messages = [];
        for (let [key, v] of Object.entries(validations)) {
            // Filter out empty messages
            messages = messages.filter(item => !!item);
            if(messages.length){
                break;
            }
            if (typeof v === 'function') {
                // provide custom validators with full access to the data.
                // supports validation like required: function (value, state) {return 'Input is required'}
                messages.push(v(value, data));
            } else if (v && typeof v.validator === 'function') {
                // custom validator with message
                if (!v.validator(value, data)) {
                    messages.push(v.message || key);
                }
            } else if (v && typeof v.asyncValidator === 'function') {
                // validate via microservice
                // only a single validation is done in this flow
                asyncValidatorsCurrentlyRunning[name] = true;
                if(eventType==='onChange'){
                    this.delayedValidation(name, ()=>v.asyncValidator(value, (errorMsg)=>this.finishAsyncValidate(name, errorMsg ? {status: 'error', messages: [errorMsg]} : null)))
                } else {
                    v.asyncValidator(value, (errorMsg)=>this.finishAsyncValidate(name, errorMsg ? {status: 'error', messages: [errorMsg]} : null))
                }
                return null
            } else {
                // standard validator where key is the standard validator name
                const f = StandardValidators[key];
                if (!f) {
                    // unrecognized validator name
                    messages.push(`Invalid validator: ${key}`);
                }
                // suppports validation like greaterOrEqual:{checkValue:28,"Value must be at least 28"}
                // where v becomes a wrapper for any number of arguments
                if (v && typeof v === 'object') {
                    if (!f(value, v)) {
                        messages.push(v.msg || `No message for ${key}`);
                    }
                } else {
                    // suppports validation like required:"Input is required"
                    if (!f(value)) {
                        const msg = (typeof v === 'string')
                            ? v
                            : (v && v.message) || `No message for ${key}`;
                        messages.push(msg);
                    }
                }
            }
        }
        // Filter out empty messages
        messages = messages.filter(item => !!item);
        return (messages.length > 0 ? {status: 'error', messages} : null);
    };

    /**
     * Callback from input fields on keypress, update state and validate
     * @param e
     * @param postProcess
     * Transaction, modify state.
     */
    onChange(e, postProcess) {
        const target = e.target,
            name = target.name,
            value = (target.type === 'checkbox' ? target.checked : target.value);
        this.setValue(name, value, postProcess, 'onChange');
    }

    /**
     * TODO: Same as onChange, refactor?
     * Callback from input fields on blur, update state and validate.
     * @param e
     * @param postProcess
     * Transaction, modify state.
     */
    onBlur(e, postProcess) {
        const target = e.target,
            name = target.name,
            value = (target.type === 'checkbox' ? target.checked : target.value);
        this.setValue(name, value, postProcess, 'onBlur');
    }

    /**
     * Register a component as React map it to DOM.
     * @param comp
     * Transaction, modify state.
     */
    registerComponent(comp) {
        const {name, validations} = comp.props,
            fields = this.state.fields;
        // if this is the first time, marked the field pristine (untouched)
        let field = fields[name];
        if (!field) {
            field = {pristine: true};
            fields[name] = field;
        }
        field.registered = true;
        field.validations = validations;
    }

    /**
     * Remove registration of a component when React remove is from DOM.
     * @param comp
     */
    deregisterComponent(comp) {
        // don't know what to do yet here...
        const {name} = comp.props,
            fields = this.state.fields;
        // if this is the first time, marked the field pristine (untouched)
        let field = fields[name];
        if (field) {
            field.registered = false;
        }
    }

    /**
     * Validate all registered fields, but do not issue state change event.
     * Nothing is returned, validation state changes
     */
    validateAllSync() {
        const state = this.state;
        // Validate all registered components
        state.validationState = {};
        for (let [name, field] of Object.entries(state.fields)) {
            if (!field.registered) continue;
            const value = this.getValue(name);
            state.validationState[name] = this.validateField(name, value, 'validateAll');
        }
    }

    /**
     * Delay a validation for later. Useful when the user is causing many onchange events by
     * typing into a field and we want to wait for a bit before processing validation. Sometimes
     * validation is computationally heavy or needs to hit an API to check the value.
     * We don't want many calls to the validation being made as the user types.
     */
    delayedValidation(name, validationFunc){
        clearTimeout(delayedValidationTimer[name]);
        delayedValidationTimer[name] = setTimeout(validationFunc, 2000);
    }

    /**
     * Checks whether an async validation is running (example: microservice validation)
     */
    checkAsyncValidation(){
        return Object.values(asyncValidatorsCurrentlyRunning).length !== 0
    }

    /**
     * Finish off an asynchronous validation by setting the validation result into state
     */
    finishAsyncValidate(name, errorData) {
        if(!errorData){
            delete asyncValidatorsCurrentlyRunning[name];
            clearTimeout(delayedValidationTimer[name]);
        } else {
            const state = this.state;
            state.validationState[name] = errorData;
            this.setState(state, ()=>{
                delete asyncValidatorsCurrentlyRunning[name];
                clearTimeout(delayedValidationTimer[name]);
            });
        }
    }

    /**
     * Validate all registered fields, and issue state change event.
     */
    validateAll() {
        this.setState((state) => {
            this.validateAllSync();
            return state;
        });
    }

    /**
     * Clear all or some of the validation state.
     * @param errors - array of fields to reset
     */
    resetErrors(errors) {
        // clear all validation errors
        this.setState(state => {
            if (!errors) {
                // clear all errors
                state.validationState = {};
            } else {
                // clear only the specified fields
                for (let e of errors) {
                    if (state.validationState[e]) {
                        state.validationState[e] = null;
                    }
                }
            }
            return state;
        });
    }

    /**
     * Clear all errors for a given field.
     * @param fieldName
     */
    resetError(fieldName) {
        this.state.validationState[fieldName] = null;
    }

    /**
     * Replace current validation state with a new error messages.
     * @param newErrors - object with fieldName as key and single or multiple messages as value.
     */
    setErrors(newErrors) {
        this.setState(state => {
            state.validationState = {};
            for (let [name, error] of Object.entries(newErrors)) {
                if (typeof error === 'string') {
                    state.validationState[name] = {status: 'error', messages: [error]}
                } else {
                    state.validationState[name] = error;
                }
            }
            return state;
        });
    }

    /**
     * Sets an error for a field. Modifies the state but does not
     * issue a state change event.
     * @param error
     * @returns boolean true if the validationState of the field was modified successfully,
     *          false otherwise
     */
    setErrorSync(error) {
        const {name, value} = error,
        state = this.state,
        field = state.fields[name];
        // field is not found in the list of fields or
        // field is not registered
        if (!name || !field || field.registered === false) {
            return false;
        }
        if (typeof value === 'string') {
            state.validationState[name] = {status: 'error', messages: [value]}
        } else {
            state.validationState[name] = value;
        }
        return true;
    }

    render() {
        return (
            <Provider value={this.state}>
                {this.props.children}
            </Provider>
        );
    }
}

export {FormValidator, Consumer};
